// Yahoo Finance API for Tel Aviv Stock Exchange (TASE)
// Uses local data.js for stock listings, live API for values/charts
// Israeli stocks on Yahoo Finance use the .TA suffix

import { Information, Indices } from '../data/data.js';

const YAHOO_BASE_URL = 'https://query1.finance.yahoo.com';
// Multiple CORS proxy options for fallback - ordered by reliability
const CORS_PROXIES = [
	{ prefix: 'https://api.allorigins.win/raw?url=', encode: true },
	{ prefix: 'https://corsproxy.io/?', encode: true },
	{ prefix: 'https://thingproxy.freeboard.io/fetch/', encode: false },
	{ prefix: 'https://api.codetabs.com/v1/proxy?quest=', encode: true },
];

async function fetchWithCors(url) {
	const headers = {
		'Accept': 'application/json',
	};
	
	// Try CORS proxies silently (errors are expected when proxies are down)
	for (const proxy of CORS_PROXIES) {
		try {
			const proxyUrl = proxy.encode 
				? proxy.prefix + encodeURIComponent(url)
				: proxy.prefix + url;
			
			const response = await fetch(proxyUrl, {
				headers: headers,
				mode: 'cors',
			});
			if (response.ok) {
				// Try to parse JSON to verify it's valid (some proxies return 200 with error text)
				let data = await response.json();
				
				// Handle allorigins wrapper (if not using /raw)
				if (proxy.prefix.includes('allorigins') && data.contents) {
					try {
						data = JSON.parse(data.contents);
					} catch (e) {
						// Silently continue to next proxy
						continue;
					}
				}
				
				return data;
			}
			// Silently continue to next proxy on non-OK status
		} catch (error) {
			// Silently continue to next proxy on error
		}
	}
	
	// Last resort: try direct fetch (might work in some environments)
	try {
		const response = await fetch(url, { headers, mode: 'cors' });
		if (response.ok) {
			return await response.json();
		}
		throw new Error('Direct fetch returned non-OK status');
	} catch (error) {
		throw new Error('All fetch attempts failed');
	}
}

// Common Hebrew to English stock name mappings for Israeli stocks
const HEBREW_TO_ENGLISH = {
	'בנק לאומי': 'bank leumi',
	'לאומי': 'leumi',
	'בנק הפועלים': 'hapoalim',
	'הפועלים': 'hapoalim',
	'בנק דיסקונט': 'discount bank',
	'דיסקונט': 'discount',
	'טבע': 'teva',
	'אלביט': 'elbit',
	'נייס': 'nice',
	'צ׳ק פוינט': 'checkpoint',
	'צ\'ק פוינט': 'checkpoint',
	'בזק': 'bezeq',
	'סלקום': 'cellcom',
	'פרטנר': 'partner',
	'תל אביב': 'ta-125',
	'מדד': 'index',
	'ת"א 35': 'ta-35',
	'ת"א 125': 'ta-125',
	'ת"א 90': 'ta-90',
	'מזרחי': 'mizrahi',
	'בנק מזרחי': 'mizrahi tefahot',
	'שופרסל': 'shufersal',
	'אזרילי': 'azrieli',
	'אמות': 'amot',
	'מליסרון': 'melisron',
	'דלק': 'delek',
	'פז': 'paz',
	'כיל': 'ice',
	'הראל': 'harel',
	'מגדל': 'migdal',
	'כלל': 'clal',
	'אי די בי': 'idb',
	'קבוצת דלק': 'delek group',
	'אלון': 'alon',
	'פניקס': 'phoenix',
	'מנורה': 'menora',
	'סטראוס': 'strauss',
	'פוקס': 'fox',
	'גולף': 'golf',
	'דלתא': 'delta',
};

// Translate Hebrew search term to English if possible
function translateHebrewQuery(query) {
	const lowerQuery = query.toLowerCase().trim();
	
	// Check for exact match first
	if (HEBREW_TO_ENGLISH[lowerQuery]) {
		return HEBREW_TO_ENGLISH[lowerQuery];
	}
	
	// Check if query contains Hebrew characters
	const hebrewRegex = /[\u0590-\u05FF]/;
	if (hebrewRegex.test(query)) {
		// Try to find partial matches
		for (const [hebrew, english] of Object.entries(HEBREW_TO_ENGLISH)) {
			if (lowerQuery.includes(hebrew) || hebrew.includes(lowerQuery)) {
				return english;
			}
		}
		// If no translation found, return null (will search local data)
		return null;
	}
	
	return query; // Return original if not Hebrew
}

// Normalize search terms for common variations
// Handles: "snp 500", "snp500", "s&p500" -> "s&p 500"
// Also handles: "nasdaq100" -> "nasdaq 100", etc.
function normalizeSearchQuery(query) {
	let normalized = query.toLowerCase().trim();
	
	// Normalize S&P 500 variations
	normalized = normalized
		.replace(/\bsnp\s*500\b/gi, 's&p 500')
		.replace(/\bs&p\s*500\b/gi, 's&p 500')
		.replace(/\bsp\s*500\b/gi, 's&p 500')
		.replace(/\bs\s*&\s*p\s*500\b/gi, 's&p 500');
	
	// Normalize Nasdaq variations
	normalized = normalized
		.replace(/\bnasdaq\s*100\b/gi, 'nasdaq 100')
		.replace(/\bnasdaq100\b/gi, 'nasdaq 100');
	
	return normalized;
}

// Search local data for stocks matching query
// Uses Hebrew and English TASE names for filtering (not Yahoo symbols)
function searchLocalData(query) {
	const lowerQuery = query.toLowerCase().trim();
	
	// Translate Hebrew if needed
	const translatedQuery = translateHebrewQuery(query);
	// Always use lowercase for comparison
	const searchQuery = (translatedQuery || lowerQuery).toLowerCase();
	
	const results = Information.filter(stock => {
		// Primary search fields: Hebrew and English TASE names
		const englishName = (stock.EnglishName || '').toLowerCase();
		const englishLongName = (stock.EnglishLongName || '').toLowerCase();
		const hebrewName = (stock.HebrewName || '').toLowerCase();
		const hebrewLongName = (stock.HebrewLongName || '').toLowerCase();
		
		// Fallback fields for backwards compatibility
		const name = (stock.Name || '').toLowerCase();
		const shortName = (stock.ShortName || '').toLowerCase();
		const taseSymbol = (stock.TaseSymbol || '').toLowerCase();
		
		// Additional useful fields
		const sector = (stock.Sector || '').toLowerCase();
		const industry = (stock.Industry || '').toLowerCase();
		
		// Search in TASE names (primary), then fallback fields
		return englishName.includes(searchQuery) ||
			   englishLongName.includes(searchQuery) ||
			   hebrewName.includes(searchQuery) ||
			   hebrewLongName.includes(searchQuery) ||
			   name.includes(searchQuery) ||
			   shortName.includes(searchQuery) ||
			   taseSymbol.includes(searchQuery) ||
			   sector.includes(searchQuery) ||
			   industry.includes(searchQuery);
	});
	
	return results;
}

// Search function compatible with existing app structure
// Uses local data file instead of live API
export async function search(
	search_keyword,
	imitating,
	leveraged,
	short,
	normal_stock,
	kosher_stock,
	mm,
	managment_fee_filter
) {
	if (!search_keyword || search_keyword.trim() === '') {
		return -1;
	}

	try {
		// Search local data
		let results = searchLocalData(search_keyword);

		if (results.length === 0) {
			return -1;
		}

		// Apply filters based on checkboxes
		let filteredResults = results;
		
		// Filter by management fee
		filteredResults = filteredResults.filter(item => {
			const totalFee = (item.ManagementFee || 0) + (item.TrusteeFee || 0);
			return totalFee <= managment_fee_filter;
		});
		
		// Filter by kosher
		if (!kosher_stock) {
			filteredResults = filteredResults.filter(item => 
				!item.Name?.includes('כשרה') && 
				!item.HebrewName?.includes('כשרה')
			);
		}
		
		// Filter by MM (market neutral funds)
		if (!mm) {
			filteredResults = filteredResults.filter(item => {
				const itemName = item.Name || '';
				const hebrewName = item.HebrewLongName || '';
				const englishName = item.EnglishName || '';
				const isMarketNeutral = itemName.includes('מנוטרל') || 
										hebrewName.includes('מנוטרל') ||
										englishName.toLowerCase().includes('hedged') ||
										englishName.toLowerCase().includes('currency hedged');
				return !isMarketNeutral;
			});
		}
		
		// Filter based on stock type
		if (!normal_stock) {
			filteredResults = filteredResults.filter(item => 
				item.Type !== '1' || item.SubType !== '1'
			);
		}
		
		// Filter by fund type indicators - fund must match at least one selected filter
		const anyFilterSelected = imitating || leveraged || short;
		if (anyFilterSelected) {
			filteredResults = filteredResults.filter(item => {
				// Skip stocks (only apply to funds/securities)
				if (item.Type === '1' && item.SubType === '1') {
					return true;
				}
				
				const hasImitatingFlag = item.FundIndicators?.IMITATING;
				const hasLeveragedFlag = item.FundIndicators?.LEVERAGED;
				const hasShortFlag = item.FundIndicators?.SHORT;
				
				const matchesFilter = (hasImitatingFlag && imitating) ||
									  (hasLeveragedFlag && leveraged) ||
									  (hasShortFlag && short);
				return matchesFilter;
			});
		}

		if (filteredResults.length === 0) {
			return -1;
		}

		return filteredResults;
	} catch (error) {
		console.error('Search error:', error);
		return -1;
	}
}

// Search Yahoo Finance API (for additional results not in local data)
export async function searchYahooFinance(query) {
	const translatedQuery = translateHebrewQuery(query);
	const searchQuery = translatedQuery || query;
	
	const searchUrl = `${YAHOO_BASE_URL}/v1/finance/search?q=${encodeURIComponent(searchQuery)}&quotesCount=50&newsCount=0&enableFuzzyQuery=true&quotesQueryId=tss_match_phrase_query`;
	
	try {
		const data = await fetchWithCors(searchUrl);
		
		if (data.quotes) {
			// Filter for Israeli stocks (.TA suffix), indices, or Tel Aviv exchange
			const israeliResults = data.quotes.filter(quote => {
				const symbol = quote.symbol || '';
				const exchange = (quote.exchange || '').toUpperCase();
				const exchDisp = (quote.exchDisp || '').toLowerCase();
				
				return symbol.endsWith('.TA') || 
					   symbol.startsWith('^TA') ||
					   exchange === 'TLV' ||
					   exchange === 'TAE' ||
					   exchDisp.includes('tel aviv');
			});
			
			return israeliResults.length > 0 ? israeliResults : data.quotes;
		}
		return [];
	} catch (error) {
		console.error('Yahoo search error:', error);
		return [];
	}
}

// Filter indices from search results
export function filter_indices(search_keyword, filtered_res) {
	if (!filtered_res || filtered_res.length === 0) {
		return [];
	}
	
	// Search local indices
	const localIndices = Indices.filter(index => 
		index.toLowerCase().includes(search_keyword.toLowerCase())
	);
	
	// Also include any indices from search results
	const searchIndices = filtered_res
		.filter(item => item.Type === '2' || item.QuoteType === 'INDEX')
		.map(item => item.Name);
	
	return [...new Set([...localIndices, ...searchIndices])];
}

// Get historical chart data for an instrument (LIVE API CALL)
export async function getChartData(symbol, period = '5y') {
	const range = period === '5y' ? '5y' : '1y';
	const interval = '1d';
	
	const chartUrl = `${YAHOO_BASE_URL}/v8/finance/chart/${encodeURIComponent(symbol)}?range=${range}&interval=${interval}&includePrePost=false`;
	
	try {
		const data = await fetchWithCors(chartUrl);
		
		if (data.chart && data.chart.result && data.chart.result[0]) {
			return data.chart.result[0];
		}
		return null;
	} catch (error) {
		// Silently return null - chart data is optional
		return null;
	}
}

// Get chart data for multiple instruments (LIVE API CALLS)
export function get_instrument_chart_data(today, instruments, raw_data) {
	const add_len = raw_data.length;
	
	for (let i = add_len; i < instruments.length; i++) {
		const instrument = instruments[i];
		// Use YahooId field for chart data retrieval
		const yahooId = instrument.YahooId;
		
		// Skip instruments without a Yahoo ID
		if (!yahooId) {
			console.debug(`No YahooId for instrument: ${instrument.Name} - chart data not available`);
			const promise = Promise.resolve(JSON.stringify({ HistoricData: [] }));
			raw_data.push(promise);
			continue;
		}
		
		const promise = getChartData(yahooId, '5y').then(chartData => {
			if (chartData) {
				// Convert Yahoo format to existing app format and stringify for compatibility
				const converted = convertYahooChartData(chartData);
				return JSON.stringify(converted);
			}
			console.debug(`No chart data returned for YahooId: ${yahooId}`);
			return JSON.stringify({ HistoricData: [] });
		}).catch(err => {
			console.debug('Error fetching chart data for', yahooId, ':', err.message);
			return JSON.stringify({ HistoricData: [] });
		});
		
		raw_data.push(promise);
	}
}

// Convert Yahoo chart data format to existing app format
function convertYahooChartData(yahooData) {
	if (!yahooData || !yahooData.timestamp || !yahooData.indicators) {
		console.log('convertYahooChartData: Invalid or empty yahooData');
		return { HistoricData: [] };
	}
	
	const timestamps = yahooData.timestamp;
	const quotes = yahooData.indicators.quote[0];
	const adjClose = yahooData.indicators.adjclose?.[0]?.adjclose || quotes.close;
	
	console.log(`convertYahooChartData: ${timestamps.length} timestamps, first=${timestamps[0]}, last=${timestamps[timestamps.length-1]}`);
	console.log(`  First close: ${adjClose[0]}, Last close: ${adjClose[adjClose.length-1]}`);
	
	const historicData = [];
	
	for (let i = 0; i < timestamps.length; i++) {
		const date = new Date(timestamps[i] * 1000);
		const day = date.getDate();
		const month = date.getMonth() + 1;
		const year = date.getFullYear();
		const dateStr = `${day}/${month}/${year}`;
		
		const close = adjClose[i] || quotes.close[i];
		
		if (close !== null && close !== undefined) {
			historicData.push({
				D: dateStr,
				P: close,
				C: close,
				O: quotes.open[i],
				H: quotes.high[i],
				L: quotes.low[i],
				V: quotes.volume[i]
			});
		}
	}
	
	// Reverse to match TASE format (newest first, oldest last)
	// The extract_chart_point function expects data sorted newest to oldest
	historicData.reverse();
	
	console.log(`  After reverse: first.D=${historicData[0]?.D}, last.D=${historicData[historicData.length-1]?.D}`);
	console.log(`  After reverse: first.P=${historicData[0]?.P}, last.P=${historicData[historicData.length-1]?.P}`);
	
	return {
		HistoricData: historicData
	};
}

// Extract chart length (number of data points and first date)
// Note: HistoricData is sorted newest to oldest (index 0 = most recent)
export function extract_chart_length(instrument_data) {
	let data = instrument_data;
	
	// Handle stringified data
	if (typeof data === 'string') {
		try {
			data = JSON.parse(data);
		} catch (e) {
			console.error('Error parsing data:', e);
			return [0, '1/1/2020'];
		}
	}
	
	if (Array.isArray(data)) {
		data = data[0];
	}
	
	if (!data || !data.HistoricData || data.HistoricData.length === 0) {
		// This is normal for instruments without Yahoo Finance coverage
		return [0, '1/1/2020'];
	}
	
	const historicData = data.HistoricData;
	let len = historicData.length;
	
	// Data is sorted newest first (index 0 = newest, last index = oldest)
	// Return the oldest date (last element) as the start date
	let oldestDate = historicData[len - 1].D;
	
	// Handle null values at the end (oldest data points)
	if (historicData[len - 1].C === null) {
		len = len - 1;
		if (len > 0) {
			oldestDate = historicData[len - 1].D;
		}
	}
	
	console.log(`extract_chart_length: ${len} points, oldestDate=${oldestDate}`);
	
	return [len, oldestDate];
}

// Helper function to convert string date to Date object
function str_to_date(str_date) {
	const s_date = str_date.split('/');
	// Format is D/M/YYYY - need to convert to MM/DD/YYYY for Date constructor
	const dateObj = new Date(parseInt(s_date[2]), parseInt(s_date[1]) - 1, parseInt(s_date[0]));
	return dateObj;
}

// Helper function to convert Date to string
function date_to_str(date, sub) {
	const day = date.getDate();
	const month = date.getMonth() + 1;
	let year = date.getFullYear();
	if (sub) {
		year = year - 2000;
	}
	return day + '/' + month + '/' + year;
}

// Fill gaps in data (weekends, holidays)
function fill_holes(j, last_known_data, array_dates_to_push, array_data_to_push, curr_date, next_date, fill_x) {
	let i = 1;
	let temp_str_date;
	let splited_date;
	let next_day_time = curr_date.getTime() + i * 26 * 60 * 60 * 1000;
	let temp_date;
	
	while (str_to_date(next_date).getTime() > next_day_time) {
		temp_date = new Date(next_day_time);
		temp_str_date = date_to_str(temp_date, false);
		splited_date = temp_str_date.split('/');
		splited_date[2] = splited_date[2] - 2000;
		
		if (temp_date.getDay() < 5 && temp_date.getDay() !== 0) {
			if (fill_x) {
				array_dates_to_push.push(splited_date[0] + '/' + splited_date[1] + '/' + splited_date[2]);
			}
			if (fill_x || array_dates_to_push.length > array_data_to_push.length) {
				array_data_to_push.push(last_known_data);
			}
		}
		i = i + 1;
		next_day_time = curr_date.getTime() + i * 26 * 60 * 60 * 1000;
	}
}

// Helper function to get display name based on language
function getDisplayName(instrument, lang) {
	const isHebrew = lang && lang.NAME === 'HEBREW';
	const id = instrument?.MayaId || instrument?.Id || '';
	
	let displayName;
	if (isHebrew && instrument?.HebrewName) {
		displayName = instrument.HebrewName;
	} else {
		displayName = instrument?.Name || instrument?.EnglishName || instrument?.Symbol || 'Unknown';
	}
	
	// Append MayaId to the name
	if (id) {
		return `${displayName} (${id})`;
	}
	return displayName;
}

// Extract chart points for graphing
export function extract_chart_point(x, y, instruments, data_array, common_date, end_date, i, fill_x, lang = null) {
	let value = data_array[i];
	
	// Handle data that might be stringified (for compatibility with existing Graph.js)
	let data = value;
	if (typeof data === 'string') {
		try {
			data = JSON.parse(data);
		} catch (e) {
			console.error('Error parsing data:', e);
		}
	}
	
	if (Array.isArray(data)) {
		data = data[0];
	}
	
	if (!data || !data.HistoricData || data.HistoricData.length === 0) {
		const instrumentName = getDisplayName(instruments[i], lang);
		// Silently return empty data for instruments without Yahoo Finance coverage
		return [x, [], instrumentName, 0];
	}
	
	const historicData = data.HistoricData;
	const name = getDisplayName(instruments[i], lang);
	y = [];
	let max = -200;
	let has_common_date = false;
	let y_0;
	let last_loop_date;
	
	// Debug: Log date range info
	console.log(`Processing ${name}: ${historicData.length} data points`);
	console.log(`  Newest date (index 0): ${historicData[0].D}`);
	console.log(`  Oldest date (last index): ${historicData[historicData.length - 1].D}`);
	console.log(`  common_date: ${common_date}, end_date: ${end_date}`);
	
	// Check if oldest date is after end_date (data doesn't cover requested period)
	const oldestDataDate = str_to_date(historicData[historicData.length - 1].D);
	const newestDataDate = str_to_date(historicData[0].D);
	if (oldestDataDate.getTime() > end_date.getTime()) {
		console.log(`  Skipping: oldest data date ${oldestDataDate} > end_date ${end_date}`);
		return [x, y, name, 0];
	}
	
	// If data doesn't span at least a week (7 days), skip this fund
	const MIN_DAYS_REQUIRED = 7;
	const days_covered = Math.ceil((newestDataDate.getTime() - oldestDataDate.getTime()) / (1000 * 3600 * 24));
	if (days_covered < MIN_DAYS_REQUIRED) {
		console.log(`  Skipping ${name}: data only covers ${days_covered} days (minimum ${MIN_DAYS_REQUIRED} required)`);
		return [x, [], name, 0];
	}
	
	for (let j = historicData.length - 1; j >= 0; j--) {
		const data_y_point = historicData[j].P;
		const dateStr = historicData[j].D;
		const date_in_loop = str_to_date(dateStr);
		
		// Debug first few iterations
		if (j >= historicData.length - 3 || j <= 2) {
			console.log(`  j=${j}: date=${dateStr}, price=${data_y_point}, y_0=${y_0}, has_common=${has_common_date}`);
		}
		
		if (date_in_loop.getTime() < common_date.getTime()) {
			last_loop_date = date_in_loop;
			continue;
		}
		
		if (date_in_loop.getTime() === common_date.getTime()) {
			has_common_date = true;
			y_0 = historicData[j].P;
		} else if (!has_common_date && date_in_loop.getTime() > common_date.getTime()) {
			// No exact match for common_date, use the first available data point after common_date as baseline
			// Data is sorted newest to oldest, so historicData[j] is the first point >= common_date
			has_common_date = true;
			y_0 = historicData[j].P;
			
			// Fill holes from common_date to the first available data point if needed
			if (fill_x) {
				x.push(date_to_str(common_date, true));
			}
			y.push(0);
			fill_holes(j, 0, x, y, common_date, date_to_str(date_in_loop, false), fill_x);
		}
		
		if (date_in_loop > end_date) {
			break;
		}
		
		// Safety check: ensure y_0 is set before calculating
		if (y_0 === undefined || y_0 === null || y_0 === 0) {
			console.log(`  Warning: y_0 not set, using current price ${data_y_point}`);
			y_0 = data_y_point;
		}
		
		let my_data = data_y_point / y_0;
		
		if (my_data > max) {
			max = my_data;
		}
		my_data = my_data - 1;
		
		y.push(my_data);
		
		if (fill_x) {
			x.push(date_to_str(date_in_loop, true));
		}
		
		if (j - 1 >= 0) {
			let next_date = historicData[j - 1].D;
			if (str_to_date(next_date) > end_date) {
				next_date = date_to_str(end_date, false);
			}
			fill_holes(j, my_data, x, y, date_in_loop, next_date, fill_x);
		}
		last_loop_date = date_in_loop;
	}
	
	if (last_loop_date) {
		fill_holes(0, y[y.length - 1] || 0, x, y, last_loop_date, date_to_str(end_date, false), fill_x);
	}
	
	console.log(`  Result: ${y.length} points, y_0=${y_0}, max=${max}`);
	
	return [x, y, name, max];
}

// Extract table information for display
export function extract_table_info(all_results, lang = null) {
	const table_data = [];
	const isHebrew = lang && lang.NAME === 'HEBREW';
	
	for (let k = 0; k < all_results.length; k++) {
		const fund_data = all_results[k];
		
		// Choose name based on language
		let displayName;
		if (isHebrew && fund_data.HebrewName) {
			displayName = fund_data.HebrewName;
		} else {
			displayName = fund_data.Name || fund_data.EnglishName;
		}
		
		const relevant_info = {
			name: displayName,
			hebrewName: fund_data.HebrewName,
			englishName: fund_data.EnglishName || fund_data.Name,
			id: fund_data.MayaId || fund_data.Id,
			mayaId: fund_data.MayaId,
			symbol: fund_data.Symbol,
			managment_fee: fund_data.ManagementFee || 0,
			var_fee: fund_data.VariableFee || 0,
			truste_fee: fund_data.TrusteeFee || 0,
			twelve_months: null,
			year_yield: null,
			month_yield: null,
			daily_yield: null,
			price: fund_data.Price || 0,
			std: fund_data.StandardDeviation || 0,
			exchange: fund_data.Exchange,
			quoteType: fund_data.QuoteType,
			sector: fund_data.Sector,
			industry: fund_data.Industry
		};
		
		table_data.push(relevant_info);
	}
	
	return table_data;
}

// Get quote data for a symbol (LIVE API CALL for current price)
export async function getQuote(symbol) {
	const quoteUrl = `${YAHOO_BASE_URL}/v7/finance/quote?symbols=${encodeURIComponent(symbol)}`;
	
	try {
		const data = await fetchWithCors(quoteUrl);
		
		if (data.quoteResponse && data.quoteResponse.result) {
			return data.quoteResponse.result[0];
		}
		return null;
	} catch (error) {
		console.error('Quote error:', error);
		return null;
	}
}

// List all available TASE stocks from local data
export function listTASEStocks() {
	return Information.filter(stock => 
		stock.Type === '1' && stock.SubType === '1'
	);
}

// List all available ETFs from local data
export function listETFs() {
	return Information.filter(stock => 
		stock.Type === '4' || stock.QuoteType === 'ETF'
	);
}

// List available indices from local data
export function listIndices() {
	return Information.filter(stock => 
		stock.Type === '2' || stock.QuoteType === 'INDEX'
	);
}

// Get all stocks from local data
export function getAllStocks() {
	return Information;
}

// Get all index names
export function getIndicesList() {
	return Indices;
}

// Autocomplete search - optimized for quick suggestions as user types
// Returns matching stocks from local data without any API calls
export function autocomplete(query, maxResults = 20) {
	if (!query || query.trim().length === 0) {
		return [];
	}
	
	const lowerQuery = query.toLowerCase().trim();
	
	// Translate Hebrew if needed
	const translatedQuery = translateHebrewQuery(query);
	// Normalize search terms (snp 500 -> s&p 500, etc.)
	const normalizedQuery = normalizeSearchQuery(translatedQuery || lowerQuery);
	// Always use lowercase for comparison
	const searchQuery = normalizedQuery.toLowerCase();
	
	// Score-based matching for better relevance
	const scoredResults = [];
	
	for (const stock of Information) {
		const name = (stock.Name || '').toLowerCase();
		const shortName = (stock.ShortName || '').toLowerCase();
		const symbol = (stock.Symbol || '').toLowerCase();
		const id = (stock.Id || '').toLowerCase();
		const mayaId = (stock.MayaId || '').toLowerCase();
		// Also normalize names for comparison (in case they contain "S&P 500" etc.)
		const normalizedName = normalizeSearchQuery(name);
		const normalizedShortName = normalizeSearchQuery(shortName);
		
		let score = 0;
		
		// Exact match on Id or MayaId (highest priority for numeric searches)
		if (mayaId === searchQuery || id === searchQuery || id === 'tase:' + searchQuery) {
			score = 100;
		}
		// Exact match on symbol (highest priority)
		else if (symbol === searchQuery || symbol === searchQuery + '.ta') {
			score = 100;
		}
		// Id/MayaId starts with query (good for partial numeric searches)
		else if (mayaId.startsWith(searchQuery) || id.startsWith('tase:' + searchQuery)) {
			score = 95;
		}
		// Symbol starts with query
		else if (symbol.startsWith(searchQuery)) {
			score = 90;
		}
		// Name starts with query (check both original and normalized)
		else if (name.startsWith(searchQuery) || shortName.startsWith(searchQuery) ||
				 normalizedName.startsWith(searchQuery) || normalizedShortName.startsWith(searchQuery)) {
			score = 80;
		}
		// Symbol contains query
		else if (symbol.includes(searchQuery)) {
			score = 70;
		}
		// Id/MayaId contains query
		else if (mayaId.includes(searchQuery) || id.includes(searchQuery)) {
			score = 65;
		}
		// Name contains query (word boundary)
		else if (name.includes(' ' + searchQuery) || shortName.includes(' ' + searchQuery) ||
				 normalizedName.includes(' ' + searchQuery) || normalizedShortName.includes(' ' + searchQuery)) {
			score = 60;
		}
		// Name contains query anywhere (check both original and normalized)
		else if (name.includes(searchQuery) || shortName.includes(searchQuery) ||
				 normalizedName.includes(searchQuery) || normalizedShortName.includes(searchQuery)) {
			score = 50;
		}
		
		if (score > 0) {
			// Boost stocks with Yahoo symbols (tradeable)
			if (stock.Symbol && stock.Symbol.endsWith('.TA')) {
				score += 5;
			}
			// Boost by type: stocks > ETFs > indices
			if (stock.Type === '1') score += 3;
			else if (stock.Type === '4') score += 2;
			else if (stock.Type === '2') score += 1;
			
			scoredResults.push({ stock, score });
		}
	}
	
	// Sort by score (descending) and return top results
	scoredResults.sort((a, b) => b.score - a.score);
	
	return scoredResults.slice(0, maxResults).map(r => r.stock);
}

// Get autocomplete suggestions for indices
export function autocompleteIndices(query, maxResults = 5) {
	if (!query || query.trim().length === 0) {
		return [];
	}
	
	const lowerQuery = query.toLowerCase().trim();
	const translatedQuery = translateHebrewQuery(query);
	// Normalize search terms (snp 500 -> s&p 500, etc.)
	const normalizedQuery = normalizeSearchQuery(translatedQuery || lowerQuery);
	// Always use lowercase for comparison
	const searchQuery = normalizedQuery.toLowerCase();
	
	const matchingIndices = Indices.filter(indexName => {
		const normalizedIndexName = normalizeSearchQuery(indexName.toLowerCase());
		return normalizedIndexName.includes(searchQuery);
	});
	
	return matchingIndices.slice(0, maxResults);
}

